import Axhelper from '@/lib/Axhelper'

/**
 * MembershipsService - Handles all memberships-related API operations
 * Separates API logic from React hooks and components
 */
class MembershipsService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			memberships: `/api/memberships/filters`,
			membershipsDetails: `/api/memberships/details`,
			membershipsDeleteOne: `/api/memberships/`,
			membershipsDeleteMany: `/api/memberships/deletemany`,
			membershipsCreate: `/api/memberships/`,
			membershipsUpdate: `/api/memberships/`,
			membershipsByCustomer: `/api/memberships/bycustomer`,
			membershipsConsume: `/api/memberships/consume`,
			membershipsUnconsume: `/api/memberships/unconsume`,
		}
	}

	async getMembershipById(membershipId) {
		try {
			if (!membershipId) {
				return { data: null, error: 'Invalid membership ID provided' }
			}

			const url = `${this.endpoints.membershipsDetails}/${encodeURIComponent(membershipId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: null, error: response.error }
			}

			return { data: response.data || null, error: null }
		} catch (error) {
			console.error('MembershipsService.getMembershipById error:', error)
			return { data: null, error: error.message || 'Failed to fetch membership' }
		}
	}

	async getMembershipsWithFilters(options = {}) {
		try {
			const { limit = 50, sortColumn = 'created', sortDirection = 'desc', filters = {} } = options

			const url = this._buildFilteredUrl({ limit, sortColumn, sortDirection, filters })
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: [], error: response.error, totalCount: 0, hasMore: false }
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			const hasMore = response.data?.length < totalCount

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore,
				headers: response.headers,
			}
		} catch (error) {
			console.error('MembershipsService.getMembershipsWithFilters error:', error)
			return { data: [], error: error.message || 'Failed to fetch memberships', totalCount: 0, hasMore: false }
		}
	}

	/**
	 * Get membership statistics
	 * @returns {Promise<{data: Object, error: string|null}>}
	 */
	async getMembershipStats() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.membershipsStats)

			if (response.error) {
				return { data: {}, error: response.error }
			}

			return { data: response.data || {}, error: null }
		} catch (error) {
			console.error('MembershipsService.getMembershipStats error:', error)
			return { data: {}, error: error.message || 'Failed to fetch membership statistics' }
		}
	}

	/**
	 * Delete multiple memberships
	 * Mirrors LeadsService strategy: single DELETE, bulk POST /deletemany with fallback
	 * @param {Array<string>} membershipIds
	 * @returns {Promise<{success: boolean, error: string|null, message: string|null}>}
	 */
	async deleteMemberships(membershipIds) {
		try {
			if (!Array.isArray(membershipIds) || membershipIds.length === 0) {
				return { success: false, error: 'Invalid membership IDs provided', message: null }
			}

			let ok = false

			if (membershipIds.length === 1) {
				const url = `${this.endpoints.membershipsDeleteOne}${membershipIds[0]}`
				const response = await Axhelper.sendDeleteAsync(url)
				ok = !response?.error
				if (!ok) return { success: false, error: response.error, message: null }
			} else {
				try {
					const bulk = await Axhelper.sendPostAsync(this.endpoints.membershipsDeleteMany, { ids: membershipIds })
					if (bulk?.error) throw new Error(bulk.error)
					ok = true
				} catch {
					const results = await Promise.allSettled(
						membershipIds.map(id => Axhelper.sendDeleteAsync(`${this.endpoints.membershipsDeleteOne}${id}`))
					)
					ok = results.every(r => r.status === 'fulfilled' && !r.value?.error)
					if (!ok) {
						const failed = results
							.map((r, i) => (r.status === 'fulfilled' && !r.value?.error ? null : membershipIds[i]))
							.filter(Boolean)
						return { success: false, error: `Failed to delete ids: ${failed.join(', ')}`, message: null }
					}
				}
			}

			const staticMsg =
				membershipIds.length > 1
					? 'Selected memberships have been deleted successfully.'
					: 'Membership has been deleted successfully.'

			return { success: true, error: null, message: staticMsg }
		} catch (error) {
			console.error('MembershipsService.deleteMemberships error:', error)
			return { success: false, error: error.message || 'Failed to delete memberships', message: null }
		}
	}

	async createMembership(membershipData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.membershipsCreate, membershipData)
			if (response.error) {
				return { success: false, error: response.error }
			}
			return { success: true, error: null, data: response.data || null }
		} catch (error) {
			console.error('MembershipsService.createMembership error:', error)
			return { success: false, error: error.message || 'Failed to create membership' }
		}
	}

	async updateMembership(membershipId, membershipData) {
		try {
			if (!membershipId) {
				return { success: false, error: 'Invalid membership ID provided' }
			}

			const url = `${this.endpoints.membershipsUpdate}/${encodeURIComponent(membershipId)}`
			const response = await Axhelper.sendPutAsync(url, membershipData)

			if (response.error) {
				return { success: false, error: response.error }
			}

			return { success: true, error: null, data: response.data || null }
		} catch (error) {
			console.error('MembershipsService.updateMembership error:', error)
			return { success: false, error: error.message || 'Failed to update membership' }
		}
	}

	async getMembershipsByCustomer(customerId) {
		try {
			if (!customerId) {
				return { data: [], error: 'Invalid customer ID provided' }
			}

			const url = `${this.endpoints.membershipsByCustomer}/${encodeURIComponent(customerId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: [], error: response.error }
			}

			return { data: response.data || [], error: null }
		} catch (error) {
			console.error('MembershipsService.getMembershipsByCustomer error:', error)
			return { data: [], error: error.message || 'Failed to fetch memberships by customer' }
		}
	}

	async consumeMembership(membershipId, payload) {
		try {
			if (!membershipId) {
				return { success: false, error: 'Invalid membership ID provided', data: null }
			}

			const url = `${this.endpoints.membershipsConsume}/${encodeURIComponent(membershipId)}`

			if (Array.isArray(payload)) {
				const list = (payload || []).filter(x => x && x.date && x.service)
				const res = await Axhelper.sendPutAsync(url, { mode: 'replace', list })
				if (res?.error) return { success: false, error: res.error, data: null }
				return { success: true, error: null, data: res.data || null }
			}

			const res = await Axhelper.sendPutAsync(url, {
				date: payload?.date,
				service: payload?.service,
			})

			if (res?.error) return { success: false, error: res.error, data: null }
			return { success: true, error: null, data: res.data || null }
		} catch (error) {
			console.error('MembershipsService.consumeMembership error:', error)
			return { success: false, error: error.message || 'Failed to consume membership', data: null }
		}
	}

	async unconsumeMembership(membershipId, consumeId) {
		try {
			if (!membershipId || !consumeId) {
				return { success: false, error: 'Invalid ids', data: null }
			}

			const url = `${this.endpoints.membershipsUnconsume}/${encodeURIComponent(membershipId)}/${encodeURIComponent(
				consumeId
			)}`

			const res = await Axhelper.sendPutAsync(url)

			if (res?.error) return { success: false, error: res.error, data: null }
			return { success: true, error: null, data: res.data || null }
		} catch (error) {
			console.error('MembershipsService.unconsumeMembership error:', error)
			return { success: false, error: error.message || 'Failed to unconsume', data: null }
		}
	}

	/**
	 * Build URL for filtered membership requests
	 * @private
	 */
	_buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
		let params = [`limit=${limit}`, `sortcol=${sortColumn}`, `sortdir=${sortDirection}`]

		// Add search text filter
		if (filters.searchText && filters.searchText.length >= 3) {
			params.push(`searchText=${encodeURIComponent(filters.searchText)}`)
		}

		// Add date filters
		if (filters.fromDate) {
			params.push(`fromDate=${encodeURIComponent(filters.fromDate)}`)
		}

		if (filters.toDate) {
			params.push(`toDate=${encodeURIComponent(filters.toDate)}`)
		}

		return `${this.endpoints.memberships}?${params.join('&')}`
	}
}

// Export a singleton instance
export default new MembershipsService()
